
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Input,
  Grid,
  Pen,
  SelectDropdown,
  LoadingIndicator,
} from '@uhg-uicl/ui-core/dist';
import RRDInventoryModal from './RRDInventoryModal';
import RRDDocumentUpload from './RRDDocumentUpload';
import RRDInventoryDetailsViewModal from './RRDInventoryDetailsViewModal';
import { ICurrentUser, IRRDInventoryDetails } from '../Infrastructure/Interfaces';
import { ColumnType } from '@uhg-uicl/ui-core/dist/Organisms/Grid/types';
import axios, { CancelTokenSource } from 'axios';
import { RootState } from '../Store/StoreInit';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import { useMsal } from '@azure/msal-react';
import * as XLSX from 'xlsx';

type UserDetailsRecord = {
  portal_Id?: string;
  roleAccess?: string | string[];
  userId?: string;
};

const PAGE_SIZE = 1000;

// --------------------------- Helpers ---------------------------

const safeParse = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const normalizeRoles = (roleAccess: string | string[] | undefined) => {
  if (!roleAccess) return new Set<string>();
  const roles = Array.isArray(roleAccess)
    ? roleAccess
    : roleAccess.split(',').map((r) => r.trim());
  return new Set(roles.map((r) => r.toUpperCase()));
};

const transactionStatusTypes: { [key: string]: string } = {
  '1': 'New',
  '2': 'File Uploaded',
  '3': 'Packaging',
  '4': 'ECGReady',
  '5': 'ValidationCompleted',
  '6': 'Completed',
  '7': 'Completed',
  '8': 'Failed',
};

function getPackageAbbreviation(key: string): string {
  return transactionStatusTypes[key] ?? key;
}

const fmtDateTime = (iso: string) =>
  new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));

const makeButton = (
  name: string,
  type: 'standard' | 'diminished' | 'destroy',
  onClick: () => void,
  disabled: boolean
) => ({
  domID: `btn-${name}-${Math.random().toString(36).slice(2)}`,
  name,
  buttonType: type,
  onClick,
  IconButton: { Pen },
  disabled,
});

const mapServerToGrid = (list: any[], handlers: any) =>
  list.map((record: any) => {
    const disabled = record.lockedBy != null;
    return {
      ...record,
      // Keep raw date for logic; use formatted string for display
      createdOnRaw: record.createdOn,
      clientName: `${record.clientName} - ${record.region}`,
      facilityNameAddr: [
        record.senderName,
        record.senderAddress,
        record.senderCity,
        record.senderState,
        record.senderZip,
      ]
        .filter(Boolean)
        .join(', '),
      sendToAddress: [
        record.receiverAddress,
        record.receiverCity,
        record.receiverState,
        record.receiverZip,
      ]
        .filter(Boolean)
        .join(', '),
      createdOn: fmtDateTime(record.createdOn),
      status: getPackageAbbreviation(String(record.transactionStatus)),
      edit: makeButton('Edit', 'diminished', () => handlers.openModal(record), disabled),
      view: makeButton(
        'View',
        'standard',
        () => handlers.openInventoryDetailsViewModal(record.transactionId),
        disabled
      ),
      // delete: makeButton(
      //   'Delete',
      //   'destroy',
      //   () => handlers.handleDelete(record.inventoryMasterId),
      //   disabled
      // ),
      // upload: makeButton(
      //   'Files',
      //   'standard',
      //   () => handlers.openUploadModal(record),
      //   disabled
      // ),
    };
  });

// --------------------------- Component ---------------------------

function ArchivedRRDInventoryPage({
  setTabIndex,
  isActive,
}: {
  setTabIndex: any;
  isActive: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDetailsViewModalOpen, setIsDetailViewModalOpen] = useState(false);
  const [uploadDocumentModalOpen, setUploadDocumentModalOpen] = useState(false);

  const [gridRecords, setGridRecords] = useState<any[]>([]);
  const [singleInventoryDetailItem, setSingleInventoryDetailItem] = useState<any[]>([]);
  const [gridHeight, setGridHeight] = useState<string>('40vh');

  const [searchFilters, setSearchFilters] = useState({
    region: '',
    status: '',
    searchText: '',
  });

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [portalIds, setPortalIds] = useState<string>('');

  const cancelSourceRef = useRef<CancelTokenSource | null>(null);

  const { accounts } = useMsal();
  const usersList: ICurrentUser[] = useSelector(
    (state: RootState) => state.usersList.usersList
  );

  // Parse user details safely
  const userdetailsArr = safeParse<UserDetailsRecord[]>('userdetails', []);
  const userdetails0 = userdetailsArr[0] ?? {};
  const userid = userdetails0.portal_Id || accounts[0]?.username || '';
  const loggeduserId = userdetails0.userId;
  const roles = normalizeRoles(userdetails0.roleAccess);
  const isUser = roles.has('USER');
  const isSupervisor = roles.has('SUPERVISOR');
  const isManager = roles.has('MANAGER');

  // Grid columns
  const columns = new Set<ColumnType>([
    { dataName: 'clientName', text: 'Client-Region', sortable: true, isSorted: 0, align: 'left' },
    // { dataName: 'region', text: 'Region', sortable: true, isSorted: 0 },
    { dataName: 'accountNumber', text: 'Account Number', sortable: true, isSorted: 0 },
  { dataName: 'accountMappingId', text: 'TokenNo', sortable: true, isSorted: 0 },
    { dataName: 'facilityNameAddr', text: 'Facility', sortable: true, isSorted: 0 },
    { dataName: 'mrn', text: 'MRN', sortable: true, isSorted: 0 },
    //{ dataName: 'requestType', text: 'Request Type', sortable: true, isSorted: 0 },
    { dataName: 'sendTo', text: 'Send To', sortable: true, isSorted: 0 },
    { dataName: 'receiverName', text: 'Name', sortable: true, isSorted: 0 },
    { dataName: 'sendToAddress', text: 'Address', sortable: true, isSorted: 0 },
    // { dataName: 'receiverCity', text: 'City', sortable: true, isSorted: 0 },
    // { dataName: 'receiverState', text: 'State', sortable: true, isSorted: 0 },
    { dataName: 'createdBy', text: 'MSID', sortable: true, isSorted: 0 },
    { dataName: 'createdOn', text: 'Created Date', sortable: true, isSorted: 0 },
    //{ dataName: 'status', text: 'Status', sortable: true, isSorted: 0 },
    //{ dataName: 'remarks', text: 'Remarks', sortable: true, isSorted: 0 },
    { dataName: 'view', text: 'View',  cellType: 'button', sortable: false, isSorted: 0, width:1},
    //{ dataName: 'delete', text: 'Delete', cellType: 'button', sortable: false, isSorted: 0, width:1 },
    //{ dataName: 'upload', text: 'File(s)', cellType: 'button', sortable: false, isSorted: 0, width:1 },
  ]);

  // Resize height with rAF
  useEffect(() => {
    const updateGridHeight = () => setGridHeight(`${Math.floor(window.innerHeight * 0.65)}px`);
    updateGridHeight();
    const onResize = () => requestAnimationFrame(updateGridHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Build portalIds based on role
  useEffect(() => {
    if (isManager) {
      setPortalIds(userid);
      return;
    }
    if (isSupervisor) {
      const msidList = usersList.filter((u) => u.superVisorId === loggeduserId);
      const ids = [userid, ...msidList.map((u) => u.portal_Id)].filter(Boolean);
      setPortalIds(ids.join(','));
    } else if (isUser) {
      setPortalIds(userid);
    }
  }, [isManager, isSupervisor, isUser, userid, usersList, loggeduserId]);

  // Role-based filter
  const applyRoleFilter = (items: IRRDInventoryDetails[]) => {
    if (isManager) return items;
    if (isSupervisor) {
      const allowed = new Set(portalIds.split(',').map((s) => s.trim()));
      return items.filter((i) => allowed.has(i.createdBy));
    }
    if (isUser) {
      return items.filter((i) => i.createdBy === userid);
    }
    return items;
  };

  // Fetch driver
useEffect(() => {
  if (!isActive || !portalIds) return;
  setPage(1);
  fetchForms(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isActive, portalIds]);

  // Fetch function with axios cancellation + mapping
  const fetchForms = async (pageNum = 1) => {
    setLoading(true);
    setPage(pageNum);

    // cancel previous request
    cancelSourceRef.current?.cancel('Operation canceled due to new request.');
    cancelSourceRef.current = axios.CancelToken.source();

    try {
      const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
      const { data } = await axios.get(`${baseurl}/Inventory`, {
        cancelToken: cancelSourceRef.current.token,
        params: {
          transactionStatuses: '6,7',
          noOfDays: 60,
          page: pageNum,
          pageSize: PAGE_SIZE,
          userIds: isManager ? null : portalIds,
        },
        headers: { accept: 'application/json' },
      });

      if (!data?.success) throw new Error('Failed to fetch forms');

      let filtered = data.result as IRRDInventoryDetails[];
      filtered = applyRoleFilter(filtered);

      const handlers = {
        openModal,
        openInventoryDetailsViewModal,
        handleDelete,
        openUploadModal,
      };
      const updatedRecords = mapServerToGrid(filtered, handlers);

      setGridRecords((prev) => (pageNum === 1 ? updatedRecords : [...prev, ...updatedRecords]));
      setHasMore(updatedRecords.length === PAGE_SIZE);
    } catch (err: any) {
      if (!axios.isCancel(err)) {
        console.error('Error fetching forms:', err);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // --------------------------- UI handlers ---------------------------

  function openModal(record: any) {
    if (!record) {
      console.error('Record is undefined. Cannot open modal.');
      return;
    }
    setSingleInventoryDetailItem(record);
    setIsOpen(true);
  }

  async function openInventoryDetailsViewModal(id: any) {
    const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
    if (!id) {
      console.error('Record is undefined. Cannot open modal.');
      return;
    }
    try {
      const { data } = await axios.get(`${baseurl}/Inventory/${id}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (data?.success) {
        setSingleInventoryDetailItem(data.result);
        setIsDetailViewModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching inventory details:', error);
    }
  }

  function openUploadModal(record: any) {
    setSingleInventoryDetailItem(record);
    setUploadDocumentModalOpen(true);
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure? This item can’t be recovered once you’ve deleted it.')) return;

    // optimistic removal
    setGridRecords((prev) => prev.filter((r) => r.inventoryMasterId !== id));

    try {
      const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
      const url = `${baseurl}/Inventory/${id}/${userid}`;
      const { data } = await axios.delete(url, { headers: { 'Content-Type': 'application/json' } });
      if (data?.success) {
        message.success('Inventory deleted successfully!');
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      message.error('Error deleting inventory');
      console.error('Error deleting inventory:', error);
      // optional rollback: refetch first page
      setPage(1);
    }
  };

  const handleSearchFilterChanges = (e: any, key: keyof typeof searchFilters) => {
    setSearchFilters((prev) => ({ ...prev, [key]: e?.value ?? '' }));
  };

  // Derived records (single source of truth for grid view)
  const searchedRecords = useMemo(() => {
    let result = gridRecords;

    if (searchFilters.region) {
      result = result.filter((r) => r.region === searchFilters.region);
    }
    if (searchFilters.status) {
      result = result.filter((r) => r.status === searchFilters.status);
    }
    if (searchFilters.searchText) {
      const q = searchFilters.searchText.toLowerCase();
      result = result.filter((r) => Object.values(r).join(' ').toLowerCase().includes(q));
    }
    return result;
  }, [gridRecords, searchFilters]);

  // Export to Excel
  function exportGridRecordsToExcel() {
  const exportData = searchedRecords.map(
    ({
      edit,
      view,
      delete: del,
      upload,
      inventoryMasterId,
      transactionId,
      sourceInventoryId,
      componentName,
      transactionStatus,
      lockedBy,
      validatedBy,
      senderInformationId,
      receiverInformationId,
      claimInformationId,
      ...rest
    }) => rest
  );

  const ws = XLSX.utils.json_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
  XLSX.writeFile(wb, 'ArchivedInventoryData.xlsx');
}

  // Initial page only once; consolidated effect above will fetch
  useEffect(() => {
    setPage(1);
  }, []);

  // Empty message
  const emptyMessage = loading ? (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <LoadingIndicator length="20px" />
      <span style={{ marginLeft: 10 }}>Loading…</span>
    </div>
  ) : (
    <div style={{ textAlign: 'center', padding: '1rem' }}>No records found</div>
  );

  // Today's added count uses createdOnRaw to avoid locale parsing issues
  const todaysAddedCount = useMemo(() => {
    const today = new Date();
    today.setDate(today.getDate() -1)
    return gridRecords.filter((record) => {
      const d = new Date(record.createdOnRaw);
      return (
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear()
      );
    }).length;
  }, [gridRecords]);

  // --------------------------- Render ---------------------------

  try {
    return (
      <>
        <div>
          {/* Filters & Actions Row */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            // alignItems: 'center',
            padding: '1rem',
            // marginRight: '3rem',
            //gap: '1rem',
          }}>
            {/* Right Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '1rem',
                marginRight: '1rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginRight: '1rem',
                  marginLeft: '1rem',
                  gap: '1rem',
                }}
              >               
                <Button buttonType="diminished" name="Export" onClick={exportGridRecordsToExcel} />
              </div>
              <Input
                domID="search-box"
                aria-label="Search"
                placeholder="Search Inventory..."
                value={searchFilters.searchText}
                onChange={(e) =>
                  setSearchFilters((prev) => ({ ...prev, searchText: e.target.value }))
                }
                style={{ width: '200px' }}
              />
            </div>
          </div>

          {/* Grid + Infinite Scroll */}
          <div>
            {/* <InfiniteScroll
              dataLength={gridRecords.length}
              next={() => fetchForms(page + 1)}
              hasMore={hasMore}
              loader={<LoadingIndicator length="20px" />}
              endMessage={<p style={{ textAlign: 'center' }}><b>No more records</b></p>}
            > */}
              <Grid
                columns={columns}
                dataTestId="test-gridDefault"
                domID="test-id"
                records={searchedRecords}
                supportSelection={false}
                selectionKey="id"
                isConfigurable
                isFixedHeader
                maxHeight={gridHeight}
                emptyGridMessage={emptyMessage}
              />
            {/* </InfiniteScroll> */}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
            {/* <Button
              buttonType="diminished"
              name="Previous"
              onClick={() => page > 1 && fetchForms(page - 1)}
              disabled={page === 1 || loading}
            />
            <span style={{ margin: '0 1rem' }}>Page {page}</span>
            <Button
              buttonType="diminished"
              name="Next"
              onClick={() => hasMore && fetchForms(page + 1)}
              disabled={!hasMore || loading}
            /> */}
            <Button
              buttonType="emphasized"
              name="Load More"
              onClick={() => fetchForms(page + 1)}
              disabled={!hasMore || loading}
            />
          </div>
          
        </div>

        {/* Modals */}
        <RRDInventoryModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          rrdInventoryItem={singleInventoryDetailItem}
          refreshData={() => fetchForms(page)}
        />
        <RRDDocumentUpload
          isOpen={uploadDocumentModalOpen}
          setIsOpen={setUploadDocumentModalOpen}
          rrdInventoryItem={singleInventoryDetailItem}
          refreshData={() => fetchForms(1)}
        />
        <RRDInventoryDetailsViewModal
          isOpen={isDetailsViewModalOpen}
          setIsOpen={setIsDetailViewModalOpen}
          rrdInventoryItem={singleInventoryDetailItem}
          setRRdInventoryItem={setSingleInventoryDetailItem}
          refreshData={() => fetchForms(1)}
        />
      </>
    );
  } catch (error) {
    console.error('Error rendering Harmony component:', error);
    return <div>Error rendering component</div>;
  }
}

export default ArchivedRRDInventoryPage;
